import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import DatePicker from 'react-datetime';
import Form from '../../../component/utilities/Forms';
import { toast } from 'react-toastify';
import JoditEditor from 'jodit-react';
import axios from 'axios';
import * as CONST from "../../../component/config/Constant";
import { AuthContext } from '../../../component/context/AuthContext';
import MultiSelect from 'react-multiple-select-dropdown-lite';
import 'react-multiple-select-dropdown-lite/dist/index.css';
import moment from 'moment';

function ApplyResignationForm({ handleResignationModalClose }) {

  const [authUserData] = useContext(AuthContext);

  let isToken = '';
  let user_id = '';
  const user = authUserData?.[0];
  if (authUserData && authUserData[0]) {
    isToken = user.token || "";
    user_id = user.user_id || 0;
  }

  const [show] = useState(true);
  const [validate, setValidate] = useState({});
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);

  const [ccOptions, setCcOptions] = useState([]);
  const [assignToOptions, setAssignToOptions] = useState([]);
  const [headOptions, setHeadOptions] = useState([]);

  const [hrManager, setHrManager] = useState('');
  const [deptHead, setDeptHead] = useState('');
  const [selectedCc, setSelectedCc] = useState('');
  const [subject, setSubject] = useState('');
  const [resignationDate, setResignationDate] = useState(new Date());
  const [reason, setReason] = useState('');
  const [offerLetter, setOfferLetter] = useState('');

  const editor = useRef(null);
  const config = useMemo(() => ({
    readonly: false,
    uploader: { insertImageAsBase64URI: true },
    toolbarButtonSize: 'medium',
    toolbarSticky: false,
    buttons: ["bold", "italic", "underline", "|", "align", "ul", "ol", "|", "undo", "redo"],
  }), []);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setInitLoading(true);
    const deptId = user?.department_id;
    if (!deptId) {
      toast.error("Department information not found.");
      setInitLoading(false);
      return;
    }

    try {
      // 1. Fetch CC users — same department as employee
      const ccResp = await axios.post(
        CONST.GET_HEAD_CC_EMAILS,
        { id: user_id, department_id: String(deptId) },
        { headers: { Authorization: `Bearer ${isToken}` } }
      );
      if (ccResp.data.status === "success") {
        setCcOptions((ccResp.data.data || []).map(u => ({ value: String(u.id), label: u.name })));
      }

      // 2. Fetch all department heads (from dashboard heads API)
      const headResp = await axios.get(CONST.GET_HEAD_USER, {
        headers: { Authorization: `Bearer ${isToken}` }
      });
      if (headResp.data.status === "success") {
        const allHeads = headResp.data.data || [];
        const deptName = user?.department_name || "";
        // HR Manager = head from HRD department
        const hrHeads = allHeads.filter(h => h.department === "HRD");
        setAssignToOptions(hrHeads);
        // Dept Head = head from employee's own department (match by name)
        const deptHeads = allHeads.filter(h => h.department === deptName);
        setHeadOptions(deptHeads);
      }
    } catch (error) {
      toast.error("Failed to fetch user list.");
    }
    setInitLoading(false);
  };

  const ValidateResignation = () => {
    const validator = Form.validator({
      subject: { value: subject, isRequired: true, label: "Subject" },
      hrManager: { value: hrManager, isRequired: true, label: "HR Manager" },
      resignationDate: { value: resignationDate, isRequired: true, label: "Resignation Date" },
      reason: { value: reason, isRequired: true, label: "Reason" },
    });
    if (validator !== null) {
      setValidate({ validate: validator.errors });
      const firstError = Object.values(validator.errors)[0];
      toast.error(firstError ? firstError[0] : "Please fill all required fields.");
      return false;
    }
    setValidate({ validate: "" });
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ValidateResignation()) return;

    setLoading(true);
    setDisabled(true);

    try {
      const formData = new FormData();
      formData.append("user_id", user_id);
      formData.append("assign_to", hrManager);
      formData.append("dept_head", deptHead || "");
      formData.append("subject", subject);
      formData.append("reason", reason);
      formData.append(
        "applied_date",
        moment(resignationDate).format("YYYY-MM-DD")
      );
      formData.append("cc", selectedCc || "");
      if (offerLetter) formData.append("document", offerLetter);

      const response = await axios.post(CONST.APPLY_FOR_RESIGNATION, formData, {
        headers: {
          Authorization: `Bearer ${isToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "error") {
        toast.error(response.data.message);
      } else {
        toast.success(response.data.message || "Resignation submitted successfully.");
        handleResignationModalClose("hide");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong. Please try again.");
    }

    setLoading(false);
    setDisabled(false);
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={() => handleResignationModalClose('hide')}
      backdrop="static"
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>Apply for Resignation</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {initLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-2 text-muted">Loading...</p>
          </div>
        ) : (
          <form autoComplete="off">
            <div className="row g-4">
              {/* Row 1: HR Manager + Dept Head */}
              <div className="col-md-6">
                <label className="form-label required">HR Manager</label>
                <select
                  className={`form-control form-select ${validate.validate?.hrManager ? "is-invalid" : ""}`}
                  value={hrManager}
                  onChange={(e) => setHrManager(e.target.value)}
                >
                  <option value="">Select HR Manager</option>
                  {assignToOptions.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} — {u.designation}</option>
                  ))}
                </select>
                {validate.validate?.hrManager && (
                  <div className="invalid-feedback d-block">{validate.validate.hrManager}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">Department Head</label>
                <select
                  className="form-control form-select"
                  value={deptHead}
                  onChange={(e) => setDeptHead(e.target.value)}
                >
                  <option value="">Select Department Head</option>
                  {headOptions.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} — {u.designation}</option>
                  ))}
                </select>
              </div>

              {/* Row 2: CC */}
              <div className="col-md-12">
                <label className="form-label">Notify (CC) — Team Lead, Manager, Colleagues</label>
                <MultiSelect
                  onChange={(val) => setSelectedCc(val)}
                  options={ccOptions}
                />
              </div>

              {/* Row 3: Subject + Date */}
              <div className="col-md-8">
                <label className="form-label required">Subject</label>
                <input
                  type="text"
                  className={`form-control ${validate.validate?.subject ? "is-invalid" : ""}`}
                  value={subject}
                  placeholder="e.g. Resignation - Personal Reasons"
                  onChange={(e) => setSubject(e.target.value)}
                />
                {validate.validate?.subject && (
                  <div className="invalid-feedback d-block">{validate.validate.subject}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label required">Resignation Date</label>
                <DatePicker
                  value={resignationDate}
                  dateFormat="DD-MM-YYYY"
                  timeFormat={false}
                  inputProps={{
                    className: `form-control ${validate.validate?.resignationDate ? "is-invalid" : ""}`,
                    placeholder: "Select date",
                    readOnly: true
                  }}
                  onChange={(date) => setResignationDate(date)}
                  isValidDate={(current) => current.isSameOrAfter(moment().startOf('day'))}
                />
                {validate.validate?.resignationDate && (
                  <div className="invalid-feedback d-block">{validate.validate.resignationDate}</div>
                )}
              </div>

              {/* Row 4: Reason */}
              <div className="col-md-12">
                <label className="form-label required">Reason for Resignation</label>
                <JoditEditor
                  ref={editor}
                  value={reason}
                  config={config}
                  onBlur={(newContent) => setReason(newContent)}
                  onChange={(newContent) => setReason(newContent)}
                />
                {validate.validate?.reason && (
                  <div className="invalid-feedback d-block">{validate.validate.reason}</div>
                )}
              </div>

              {/* Row 5: Document */}
              <div className="col-md-12">
                <label className="form-label">Supporting Document (Optional)</label>
                <input type="file"
                  className="form-control"
                  accept="application/msword, application/pdf, image/*"
                  onChange={(e) => setOfferLetter(e.target.files[0])} />
                <small className="text-muted">Accepted: PDF, Word, Images (PNG, JPG)</small>
              </div>

              {/* Submit */}
              <div className="col-md-12 mt-3 pt-2">
                <div className="d-flex justify-content-end gap-3 border-top pt-4">
                  <button type="button" className="btn btn-outline-secondary mr-2"
                    onClick={() => handleResignationModalClose('hide')} disabled={loading}>
                    Discard
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={disabled || loading}>
                    {loading ? (
                      <><span className="spinner-border spinner-border-sm me-2 mr-2" role="status"></span>Submitting...</>
                    ) : "Submit Resignation"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ApplyResignationForm;
